import * as THREE from 'three/webgpu';
import type { ImportedModelRaycastResolver } from '../../imported-model/selection/ImportedModelRaycastResolver';
import { MoveAlongEntityReferenceResolver } from '../../assembly/move-component/move';
import { LinearComponentPatternDirectionReferenceResolver } from '../../pattern/internal';
import { isFeatureFace, isFeatureLine, isFeaturePoint } from '../../mesh/feature';
import { LINEAR_COMPONENT_PATTERN_DIRECTION_REFERENCE_SLOTS, LinearComponentPatternSelectionSlot } from '../../pattern';
import type { EntitySelectionManager } from '../EntitySelectionManager';
import type { RawPointerEventPayload } from '../../viewer/moudles/EventManager';
import { AssemblyEntitySelectionHandler } from './AssemblyEntitySelectionHandler';
import type { AssemblyEntitySelectionHandlerOptions } from './AssemblyEntitySelectionHandler';

/**
 * 线性零部件阵列选择处理器。
 *
 * 设计说明：
 * 1. 线性零部件阵列需要选择方向、参考几何、源零部件和实例覆盖对象；
 * 2. 当前阶段复用装配体 imported-model 拾取能力，但禁止直接拖拽零部件；
 * 3. 选择结果写入 `LinearComponentPatternController`，后续 PropertyManager 可从控制器同步状态。
 */
export class LinearComponentPatternEntitySelectionHandler extends AssemblyEntitySelectionHandler {
    //------ 线性零部件阵列-方向箭头 开始------
    /**
     * 方向 1 箭头槽位键。
     */
    private static readonly DIRECTION1_ARROW_SLOT_KEY = 'linear-component-pattern-direction-1';

    /**
     * 方向 2 箭头槽位键。
     */
    private static readonly DIRECTION2_ARROW_SLOT_KEY = 'linear-component-pattern-direction-2';

    /** 方向箭头主色。 */
    private static readonly DIRECTION_ARROW_COLOR = 0x2f8cff;

    /** 方向箭头杆身半径像素尺寸。 */
    private static readonly DIRECTION_ARROW_SHAFT_RADIUS_PIXELS = 2;

    /** 方向箭头杆身长度像素尺寸。 */
    private static readonly DIRECTION_ARROW_SHAFT_LENGTH_PIXELS = 46;

    /** 方向箭头锥头半径像素尺寸。 */
    private static readonly DIRECTION_ARROW_HEAD_RADIUS_PIXELS = 8;

    /** 方向箭头锥头长度像素尺寸。 */
    private static readonly DIRECTION_ARROW_HEAD_LENGTH_PIXELS = 18;

    /** 方向箭头底部短尾巴像素尺寸。 */
    private static readonly DIRECTION_ARROW_BOTTOM_LENGTH_PIXELS = 3;

    /** 方向箭头上方杆身像素尺寸。 */
    private static readonly DIRECTION_ARROW_TOP_LENGTH_PIXELS = 43;

    /** 方向箭头锚点抬升像素尺寸。 */
    private static readonly DIRECTION_ARROW_ANCHOR_LIFT_PIXELS = 4;

    /**
     * 方向箭头最小参考尺寸。
     *
     * 与基准面法线箭头保持同量级，避免短边面或短线参考时箭头过小。
     */
    private static readonly DIRECTION_ARROW_MIN_REFERENCE_SIZE = 0.1;

    //------ 线性零部件阵列-方向槽位候选过滤 开始------
    /**
     * 需要提供可解析方向的引用槽位集合，与 SDK 写入规则保持一致。
     *
     * 设计说明：
     * 1. 方向选择需要能提取方向向量的实体（边、面、基准面等），
     *    特征点（顶点）不提供方向信息，不应参与方向槽位的选择和 hover；
     * 2. "到参考"模式的槽位（ReferenceGeometry / SelectedReference）需要允许选择特征点
     *    作为参考目标，因此不在过滤集合中。
     */
    private static readonly DIRECTION_REFERENCE_SLOTS: ReadonlySet<LinearComponentPatternSelectionSlot>
        = new Set(LINEAR_COMPONENT_PATTERN_DIRECTION_REFERENCE_SLOTS);

    /** 点击不具备线性方向的对象时提示，不覆盖原有合法引用。 */
    private static readonly DIRECTION_REFERENCE_WARNING = '请选择可解析方向的直线、圆形边、平面、基准轴或基准面';

    /** 独立旋转轴尚未扩展圆边引用，提示必须与实际槽位规则一致。 */
    private static readonly ROTATION_AXIS_WARNING = '请选择可解析方向的直线、平面、基准轴或基准面';

    /**
     * “所选参考”槽位集合。
     *
     * 设计说明：
     * 1. 该槽位参与“到参考”的距离起算点求解；
     * 2. 只能选择源零部件自身的特征点、特征线或特征面，禁止选择其他零部件或普通基准对象。
     */
    private static readonly SELECTED_REFERENCE_SLOTS: ReadonlySet<LinearComponentPatternSelectionSlot> = new Set([
        LinearComponentPatternSelectionSlot.Direction1SelectedReference,
        LinearComponentPatternSelectionSlot.Direction2SelectedReference,
    ]);

    /**
     * “参考几何”槽位集合。
     *
     * 设计说明：
     * 1. 参考几何用于“到参考”求解终点；
     * 2. 该几何必须与当前阵列方向形成有效垂直关系，否则投影求解会产生非 SolidWorks 语义的结果。
     */
    private static readonly REFERENCE_GEOMETRY_SLOTS: ReadonlySet<LinearComponentPatternSelectionSlot> = new Set([
        LinearComponentPatternSelectionSlot.Direction1ReferenceGeometry,
        LinearComponentPatternSelectionSlot.Direction2ReferenceGeometry,
    ]);

    /** 所选参考不是特征点线面时的提示。 */
    private static readonly SELECTED_REFERENCE_FEATURE_WARNING = '所选参考必须选择已选零部件上的特征点、特征线或特征面';

    /** 尚未选择阵列源零部件时的提示。 */
    private static readonly SELECTED_REFERENCE_COMPONENT_WARNING = '请先选择要阵列的零部件，再选择该零部件上的特征点、特征线或特征面';

    /** 参考几何与阵列方向不垂直时的提示。 */
    private static readonly REFERENCE_GEOMETRY_NOT_PERPENDICULAR_WARNING = '所选参考不垂直于阵列方向。';

    /** 零部件槽位无法从当前命中解析组件根时的提示。 */
    private static readonly COMPONENT_ROOT_RESOLUTION_WARNING = '无法从当前选择解析要阵列的零部件，请选择完整零部件';

    /** 方向关系判断容差。 */
    private static readonly REFERENCE_GEOMETRY_ORTHOGONAL_EPSILON = 1e-4;
    //------ 线性零部件阵列-方向槽位候选过滤 结束------

    /** 传播方向几何解析器，圆形边使用圆轴。 */
    private readonly directionReferenceResolver: LinearComponentPatternDirectionReferenceResolver;

    /** 到参考和独立旋转轴保持原有线性几何规则。 */
    private readonly linearReferenceResolver: MoveAlongEntityReferenceResolver;

    /** 包围盒复用对象，避免高频点击时重复 new。 */
    private readonly tempBoundingBox = new THREE.Box3();

    /** 包围盒尺寸复用对象。 */
    private readonly tempBoundingSize = new THREE.Vector3();

    //------ 线性零部件阵列-方向箭头 结束------

    /**
     * 创建线性零部件阵列选择处理器。
     *
     * @param entitySelectionManager 实体选择管理器。
     * @param importedModelRaycastResolver 导入模型射线解析器。
     * @param options 处理器行为选项。
     */
    public constructor(
        entitySelectionManager: EntitySelectionManager,
        importedModelRaycastResolver: ImportedModelRaycastResolver,
        options: AssemblyEntitySelectionHandlerOptions,
    ) {
        super(entitySelectionManager, importedModelRaycastResolver, options);
        //------ 线性零部件阵列-方向箭头 开始------
        this.linearReferenceResolver = new MoveAlongEntityReferenceResolver(this.entitySelectionManager.renderer);
        this.directionReferenceResolver = new LinearComponentPatternDirectionReferenceResolver(this.linearReferenceResolver);

        // 订阅控制器选择变化事件，当引用被显式清除时（UI 右键“消除选中”），
        // 同步清除箭头并刷新持久高亮集合，避免 UI 已清空但 3D 高亮残留。
        // Handler 与 EntitySelectionManager 同生命周期，控制器在 Viewer 构建时先于 ESM 初始化，
        // 两者生命周期一致，控制器 dispose 时会自动清理所有监听器，无需手动取消订阅。
        this.entitySelectionManager.renderer.linearComponentPatternController?.onSelectionChange(
            (payload) => {
                if (payload.objectId !== null) {
                    this.restoreDirectionArrowForSelectionPayload(payload.activeSelectionSlot);
                    this.restorePersistedPatternSelectionState();
                    this.entitySelectionManager.renderer.requestRender?.();
                    return;
                }
                if (payload.activeSelectionSlot === LinearComponentPatternSelectionSlot.Direction1Reference) {
                    this.entitySelectionManager.renderer.patternDirectionArrowManager.clearArrow(
                        LinearComponentPatternEntitySelectionHandler.DIRECTION1_ARROW_SLOT_KEY,
                    );
                } else if (payload.activeSelectionSlot === LinearComponentPatternSelectionSlot.Direction1RotationAxis) {
                    //------ 线性零部件阵列-旋转实例 开始------
                    // 旋转轴当前只保存高亮引用，不创建方向箭头，因此清空时只需恢复选择态。
                    //------ 线性零部件阵列-旋转实例 结束------
                } else if (payload.activeSelectionSlot === LinearComponentPatternSelectionSlot.Direction2Reference) {
                    this.entitySelectionManager.renderer.patternDirectionArrowManager.clearArrow(
                        LinearComponentPatternEntitySelectionHandler.DIRECTION2_ARROW_SLOT_KEY,
                    );
                }
                this.restorePersistedPatternSelectionState();
                this.entitySelectionManager.renderer.requestRender?.();
            },
        );
        //------ 线性零部件阵列-方向箭头 结束------
    }

    /**
     * 根据选择变化载荷恢复方向箭头。
     *
     * 设计说明：
     * 1. 用户点击方向引用时会立即走点击链路创建箭头；
     * 2. 编辑特征恢复参数时不会触发点击链路，因此需要通过控制器事件补一次箭头重建；
     * 3. 这里只处理方向 1 / 方向 2，旋转轴不显示传播方向箭头。
     *
     * @param slot 当前选择槽位。
     */
    private restoreDirectionArrowForSelectionPayload(slot: LinearComponentPatternSelectionSlot): void {
        const controller = this.entitySelectionManager.renderer.linearComponentPatternController;
        if (!controller) {
            return;
        }
        if (slot === LinearComponentPatternSelectionSlot.Direction1Reference) {
            const directionObject = controller.getReferenceObjectBySlot(LinearComponentPatternSelectionSlot.Direction1Reference);
            if (directionObject) {
                this.updateDirectionArrowForObject(
                    directionObject,
                    LinearComponentPatternEntitySelectionHandler.DIRECTION1_ARROW_SLOT_KEY,
                );
            }
            return;
        }
        if (slot === LinearComponentPatternSelectionSlot.Direction2Reference) {
            const directionObject = controller.getReferenceObjectBySlot(LinearComponentPatternSelectionSlot.Direction2Reference);
            if (directionObject) {
                this.updateDirectionArrowForObject(
                    directionObject,
                    LinearComponentPatternEntitySelectionHandler.DIRECTION2_ARROW_SLOT_KEY,
                );
            }
        }
    }

    //------ 线性零部件阵列-方向槽位候选过滤 开始------
    /**
     * 处理鼠标移动。
     *
     * 设计说明：
     * 方向引用槽位激活时，hover 拾取结果为 FeaturePoint 时需要丢弃，
     * 因为方向选择需要能提取方向向量的实体（边、面等），特征点不提供方向信息。
     * FeaturePoint 是在 ImportedModelTopologyPickResolver 中动态创建的代理对象，
     * 无法通过候选收集阶段过滤，必须在拾取结果上进行后置过滤。
     *
     * @param clientX 最新客户端 X 坐标。
     * @param clientY 最新客户端 Y 坐标。
     */
    public override consumeHoverInput(clientX: number, clientY: number): void {
        // 特定槽位需要后置过滤拓扑代理对象，因此不能直接复用父类默认 hover 链路。
        if (this.shouldFilterTopologyProxyInHover()) {
            const hoveredPick = this.pickEntityAt(
                clientX,
                clientY,
                this.collectHoverSelectionTargets(),
            );
            // 后置过滤：根据当前槽位丢弃不允许的拓扑代理对象命中。
            const filteredPick = this.filterTopologyProxyPick(hoveredPick);
            this.updateHoverState(this.resolveHoverPickWithSelectionExclusion(filteredPick));
            return;
        }
        // 按键/拖拽仍由父类即时处理；这里只有无按键坐标查询。
        super.consumeHoverInput(clientX, clientY);
    }

    /**
     * 判断当前是否处于方向引用槽位。
     *
     * @returns 当前槽位为方向引用槽位时返回 `true`。
     */
    private isDirectionReferenceSlotActive(): boolean {
        return LinearComponentPatternEntitySelectionHandler.DIRECTION_REFERENCE_SLOTS.has(
            this.resolveActiveSelectionSlot(),
        );
    }

    /**
     * 判断当前 hover 是否需要对拓扑代理对象做后置过滤。
     *
     * @returns 需要过滤时返回 `true`。
     */
    private shouldFilterTopologyProxyInHover(): boolean {
        const activeSlot = this.resolveActiveSelectionSlot();
        // 方向槽位只做类型判断；圆形边允许候选，实际几何在 SDK 写入前校验。
        if (LinearComponentPatternEntitySelectionHandler.DIRECTION_REFERENCE_SLOTS.has(activeSlot)) {
            return true;
        }
        // 零部件槽位：只接受 FeatureFace（过滤 FeatureLine / FeaturePoint）
        if (activeSlot === LinearComponentPatternSelectionSlot.ComponentsToPattern) {
            return true;
        }
        return false;
    }

    /**
     * 根据当前槽位过滤拓扑代理拾取结果。
     *
     * 设计说明：
     * FeaturePoint / FeatureLine / FeatureFace 是在 ImportedModelTopologyPickResolver 中
     * 动态创建的代理对象，无法在候选收集阶段过滤，必须在拾取结果上后置过滤。
     *
     * @param pick 原始拾取结果。
     * @returns 过滤后的拾取结果；不符合当前槽位约束时返回 `null`。
     */
    private filterTopologyProxyPick(
        pick: THREE.Intersection<THREE.Object3D> | null,
    ): THREE.Intersection<THREE.Object3D> | null {
        if (!pick) {
            return null;
        }
        const activeSlot = this.resolveActiveSelectionSlot();
        // 类型判断不构造世界平面/方向向量；实际写入由 SDK 再校验几何有效性。
        if (LinearComponentPatternEntitySelectionHandler.DIRECTION_REFERENCE_SLOTS.has(activeSlot)) {
            const resolver = activeSlot === LinearComponentPatternSelectionSlot.Direction1RotationAxis
                ? this.linearReferenceResolver : this.directionReferenceResolver;
            return resolver.isSupportedReferenceObject(pick.object) ? pick : null;
        }
        // 零部件槽位：只接受 FeatureFace 和非拓扑代理对象（原始 imported model）
        if (activeSlot === LinearComponentPatternSelectionSlot.ComponentsToPattern) {
            if (isFeatureLine(pick.object) || isFeaturePoint(pick.object)) {
                return null;
            }
        }
        return pick;
    }
    //------ 线性零部件阵列-方向槽位候选过滤 结束------

    /**
     * 处理鼠标按下。
     *
     * @param payload 原始事件。
     */
    public override onMouseDown(payload: RawPointerEventPayload): void {
        if (payload.button !== 0) {
            return;
        }

        // 线性零部件阵列只记录选择，不允许沿用装配体默认拖拽入口，避免误触移动源零部件。
        this.entitySelectionManager.setMouseDownStart(new THREE.Vector2(payload.clientX, payload.clientY));
        this.entitySelectionManager.setIsBoxDragging(false);
        this.entitySelectionManager.setBoxDragEnd(null);
        this.resetSelectionBoxVisual();
        this.entitySelectionManager.setSuppressNextClick(false);
    }

    /**
     * 处理鼠标点击。
     *
     * @param payload 原始事件。
     */
    public override onClick(payload: RawPointerEventPayload): void {
        if (payload.button !== 0) {
            return;
        }
        if (this.entitySelectionManager.getSuppressNextClick()) {
            this.entitySelectionManager.setSuppressNextClick(false);
            return;
        }

        const picked = this.resolvePatternPick(payload);
        if (!picked) {
            if (!payload.ctrlKey) {
                // 空白点击时不能清空全部高亮，需要保持已持久化的方向参考 + 已选零部件高亮。
                // 直接调用 updateSelectionState([]) 会导致方向参考特征失去高亮，因此改为恢复持久化集合。
                const persistedObjects = this.resolveAllPersistedPatternObjects();
                if (persistedObjects.length > 0) {
                    const persistedIntersections = persistedObjects.map((obj) => ({
                        distance: 0,
                        object: obj,
                        point: obj.getWorldPosition(new THREE.Vector3()),
                    }));
                    this.entitySelectionManager.updateSelectionState(persistedIntersections);
                } else {
                    this.entitySelectionManager.updateSelectionState([]);
                }
            }
            return;
        }

        const activeSlot = this.resolveActiveSelectionSlot();

        //------ 线性零部件阵列-跳过实例预览 开始------
        if (activeSlot === LinearComponentPatternSelectionSlot.SkippedInstances) {
            const controller = this.entitySelectionManager.renderer.linearComponentPatternController;
            const markerObject = picked.object;
            controller?.setSelectionForActiveSlot(markerObject);
            this.restorePersistedPatternSelectionState();
            this.entitySelectionManager.renderer.requestRender?.();
            return;
        }
        //------ 线性零部件阵列-跳过实例预览 结束------


        // 根据当前 PropertyManager 槽位，将拓扑命中折叠为方向参考或源零部件语义对象。
        const selectionObject = this.resolvePatternSelectionObject(picked.object);
        if (!selectionObject) {
            // 零部件槽位必须失败关闭，禁止把 FeatureFace 等拓扑代理写入组件 ID 列表。
            this.notifyPatternSelectionWarning(
                LinearComponentPatternEntitySelectionHandler.COMPONENT_ROOT_RESOLUTION_WARNING,
            );
            this.restorePersistedPatternSelectionState();
            this.entitySelectionManager.renderer.requestRender?.();
            return;
        }

        //------ 线性零部件阵列-所选参考归属校验 开始------
        // “所选参考”用于源零部件自身几何到参考几何的偏移计算，必须拒绝其他零部件上的拓扑代理。
        const selectedReferenceValidation = this.validateSelectedReferenceSelection(activeSlot, picked.object);
        if (selectedReferenceValidation.accepted === false) {
            this.notifyPatternSelectionWarning(selectedReferenceValidation.message);
            this.restorePersistedPatternSelectionState();
            this.entitySelectionManager.renderer.requestRender?.();
            return;
        }
        //------ 线性零部件阵列-所选参考归属校验 结束------

        //------ 线性零部件阵列-参考几何垂直校验 开始------
        // 到参考模式的参考几何必须与阵列方向构成可投影的垂直关系，错误引用直接拒绝写入。
        const referenceGeometryValidation = this.validateReferenceGeometrySelection(activeSlot, picked.object);
        if (referenceGeometryValidation.accepted === false) {
            this.notifyPatternSelectionWarning(referenceGeometryValidation.message);
            this.restorePersistedPatternSelectionState();
            this.entitySelectionManager.renderer.requestRender?.();
            return;
        }
        //------ 线性零部件阵列-参考几何垂直校验 结束------

        //------ 线性零部件阵列-持久高亮 开始------
        // 新选对象写入槽位前先更新控制器，这样后面才能从控制器读到最新全集。
        const controller = this.entitySelectionManager.renderer.linearComponentPatternController;
        if (controller?.setSelectionForActiveSlot(selectionObject) === false) {
            this.notifyPatternSelectionWarning(
                activeSlot === LinearComponentPatternSelectionSlot.Direction1RotationAxis
                    ? LinearComponentPatternEntitySelectionHandler.ROTATION_AXIS_WARNING
                    : LinearComponentPatternEntitySelectionHandler.DIRECTION_REFERENCE_SLOTS.has(activeSlot)
                    ? LinearComponentPatternEntitySelectionHandler.DIRECTION_REFERENCE_WARNING
                    : LinearComponentPatternEntitySelectionHandler.COMPONENT_ROOT_RESOLUTION_WARNING,
            );
            this.restorePersistedPatternSelectionState();
            this.entitySelectionManager.renderer.requestRender?.();
            return;
        }

        // 将控制器中所有已选对象（方向/参考几何/所选参考 + 已选零部件）和本次新选对象合并后
        // 统一传给 updateSelectionState，确保切换槽位后之前选中的对象仍保持高亮。
        const persistedObjects = this.resolveAllPersistedPatternObjects();
        const mergedIntersections = this.mergeIntoUniqueIntersections(persistedObjects, selectionObject);
        this.entitySelectionManager.updateSelectionState(mergedIntersections);
        //------ 线性零部件阵列-持久高亮 结束------

        //------ 线性零部件阵列-选中即计算 开始------
        // 选择层主动触发预览计算，不依赖 UI 层驱动。
        // setSelectionForActiveSlot 只负责状态写入与通知，计算由此处显式发起。
        controller?.triggerPreviewUpdate();
        this.entitySelectionManager.renderer.requestRender?.();
        //------ 线性零部件阵列-选中即计算 结束------

        //------ 线性零部件阵列-方向箭头 开始------
        // 方向槽位选中后更新对应箭头；零部件槽位只高亮，不显示箭头。
        if (activeSlot === LinearComponentPatternSelectionSlot.Direction1Reference) {
            this.updateDirectionArrowForObject(
                selectionObject,
                LinearComponentPatternEntitySelectionHandler.DIRECTION1_ARROW_SLOT_KEY,
            );
        } else if (activeSlot === LinearComponentPatternSelectionSlot.Direction2Reference) {
            this.updateDirectionArrowForObject(
                selectionObject,
                LinearComponentPatternEntitySelectionHandler.DIRECTION2_ARROW_SLOT_KEY,
            );
        }
        //------ 线性零部件阵列-方向箭头 结束------
    }

    /**
     * 从控制器状态中恢复所有已保存对象的场景引用。
     *
     * 设计说明：
     * 1. 控制器只存储 uuid，需要通过场景 getObjectByProperty 还原对象引用；
     * 2. 失效对象（已离场）会返回 undefined，需要过滤；
    * 3. 所有引用类槽位（方向/参考几何/所选参考）和零部件 ID 均需合并。
     *
     * @returns 当前所有已保存对象的场景引用列表。
     */
    private resolveAllPersistedPatternObjects(): THREE.Object3D[] {
        const controller = this.entitySelectionManager.renderer.linearComponentPatternController;
        const state = controller?.getViewModel?.().state;
        if (!state) {
            return [];
        }

        const scene = this.entitySelectionManager.renderer.scene;
        const result: THREE.Object3D[] = [];

        const referenceSlots = [
            LinearComponentPatternSelectionSlot.Direction1Reference,
            LinearComponentPatternSelectionSlot.Direction1RotationAxis,
            LinearComponentPatternSelectionSlot.Direction1ReferenceGeometry,
            LinearComponentPatternSelectionSlot.Direction1SelectedReference,
            LinearComponentPatternSelectionSlot.Direction2Reference,
            LinearComponentPatternSelectionSlot.Direction2ReferenceGeometry,
            LinearComponentPatternSelectionSlot.Direction2SelectedReference,
        ];

        for (const slot of referenceSlots) {
            const liveObject = controller.getReferenceObjectBySlot?.(slot);
            if (liveObject) {
                result.push(liveObject);
                continue;
            }

            const objectId = slot === LinearComponentPatternSelectionSlot.Direction1Reference
                ? state.direction1ReferenceObjectId
                : slot === LinearComponentPatternSelectionSlot.Direction1RotationAxis
                  ? state.direction1RotationAxisObjectId
                  : slot === LinearComponentPatternSelectionSlot.Direction1ReferenceGeometry
                    ? state.direction1ReferenceGeometryObjectId
                    : slot === LinearComponentPatternSelectionSlot.Direction1SelectedReference
                      ? state.direction1SelectedReferenceObjectId
                      : slot === LinearComponentPatternSelectionSlot.Direction2Reference
                        ? state.direction2ReferenceObjectId
                        : slot === LinearComponentPatternSelectionSlot.Direction2ReferenceGeometry
                          ? state.direction2ReferenceGeometryObjectId
                          : state.direction2SelectedReferenceObjectId;
            if (!objectId) {
                continue;
            }

            const object = scene.getObjectByProperty('uuid', objectId);
            if (object) {
                result.push(object);
            }
        }

        // 零部件槽位支持多选，逐一恢复。
        for (const id of state.componentObjectIds) {
            const obj = scene.getObjectByProperty('uuid', id);
            if (obj) {
                result.push(obj);
            }
        }

        return result;
    }

    /**
     * 将已保存对象列表和本次新选对象合并为去重的 Intersection 列表。
     *
     * @param persisted 已持久化的场景对象列表。
     * @param current 本次点击命中的对象。
     * @returns 合并去重后的 Intersection 列表。
     */
    private mergeIntoUniqueIntersections(
        persisted: THREE.Object3D[],
        current: THREE.Object3D,
    ): THREE.Intersection<THREE.Object3D>[] {
        const seen = new Set<string>();
        const result: THREE.Intersection<THREE.Object3D>[] = [];

        // 当前新选的对象优先排在首位（作为主选对象）。
        seen.add(current.uuid);
        result.push({
            distance: 0,
            object: current,
            point: current.getWorldPosition(new THREE.Vector3()),
        });

        for (const obj of persisted) {
            if (seen.has(obj.uuid)) {
                continue;
            }
            seen.add(obj.uuid);
            result.push({
                distance: 0,
                object: obj,
                point: obj.getWorldPosition(new THREE.Vector3()),
            });
        }

        return result;
    }

    /**
     * 线性零部件阵列模式禁用框选。
     *
     * @returns 始终返回 `false`。
     */
    protected override isBoxSelectionEnabled(): boolean {
        return this.resolveActiveSelectionSlot() === LinearComponentPatternSelectionSlot.SkippedInstances;
    }

    /**
     * 执行框选。
     *
     * @param start 鼠标按下起点。
     * @param end 鼠标抬起终点。
     * @param _isCtrlPressed 是否按下 Ctrl。
     */
    protected override performBoxSelection(start: THREE.Vector2, end: THREE.Vector2, _isCtrlPressed: boolean): void {
        const activeSlot = this.resolveActiveSelectionSlot();
        if (activeSlot !== LinearComponentPatternSelectionSlot.SkippedInstances) {
            return;
        }

        const controller = this.entitySelectionManager.renderer.linearComponentPatternController;
        const container = this.entitySelectionManager.renderer.container;
        const camera = this.entitySelectionManager.renderer.camera;
        if (!controller || !container || !camera) {
            return;
        }

        const containerRect = container.getBoundingClientRect();
        const startX = start.x - containerRect.left;
        const startY = start.y - containerRect.top;
        const endX = end.x - containerRect.left;
        const endY = end.y - containerRect.top;
        const selectionRect = {
            minX: Math.min(startX, endX),
            minY: Math.min(startY, endY),
            maxX: Math.max(startX, endX),
            maxY: Math.max(startY, endY),
        };
        const isCrossing = endX < startX;

        const selectedMarkers: THREE.Object3D[] = [];
        const selectedInstanceKeys: string[] = [];
        const seenInstanceKeys = new Set<string>();
        controller.getSkipInstanceMarkerObjects().forEach((markerObject) => {
            const markerRect = this.computeMarkerScreenRect(markerObject, camera, containerRect);
            if (!markerRect) {
                return;
            }

            const hit = isCrossing
                ? this.patternRectanglesIntersect(selectionRect, markerRect)
                : this.patternRectangleContains(selectionRect, markerRect);
            if (!hit) {
                return;
            }

            const instanceKey = controller.resolveSkipInstanceKeyByMarker(markerObject);
            if (!instanceKey || seenInstanceKeys.has(instanceKey)) {
                return;
            }

            seenInstanceKeys.add(instanceKey);
            selectedInstanceKeys.push(instanceKey);
            selectedMarkers.push(markerObject);
        });

        if (selectedInstanceKeys.length === 0) {
            this.restorePersistedPatternSelectionState();
            return;
        }

        //------ 线性零部件阵列-跳过实例预览 开始------
        // 跳过槽位默认采用 toggle 语义：框到同一 marker 两次会在隐藏/显示之间切换。
        controller.toggleSkippedInstanceKeys(selectedInstanceKeys);
        this.restorePersistedPatternSelectionState();
        this.publishBoxSelectionEvents(selectedMarkers, isCrossing, selectionRect);
        //------ 线性零部件阵列-跳过实例预览 结束------
    }

    /**
     * 解析当前槽位应写入的选择对象。
     *
     * @param object 当前命中对象。
     * @returns 当前槽位对应的语义对象；零部件根无法解析时返回 null。
     */
    private resolvePatternSelectionObject(object: THREE.Object3D): THREE.Object3D | null {
        const activeSlot = this.resolveActiveSelectionSlot();
        if (activeSlot === LinearComponentPatternSelectionSlot.SkippedInstances) {
            return object;
        }
        if (
            activeSlot === LinearComponentPatternSelectionSlot.ComponentsToPattern
            || activeSlot === LinearComponentPatternSelectionSlot.ModifiedInstances
        ) {
            // 零部件相关槽位必须写入可变换根对象，不能把面、边、点代理当作阵列源。
            const transformRoot = this.importedModelRaycastResolver.resolveTransformRootObject(object);
            if (transformRoot) {
                return transformRoot;
            }
            if (isFeatureFace(object) || isFeatureLine(object) || isFeaturePoint(object)) {
                return null;
            }
            const semanticObject = this.importedModelRaycastResolver.resolveSelectionObject(object);
            if (semanticObject) {
                return semanticObject;
            }
            // 非 imported 普通装配对象没有选择代理时，对象自身就是合法组件候选。
            return this.importedModelRaycastResolver.isImportedModelObject(object) ? null : object;
        }

        // 方向和参考槽位保留原始命中对象，后续求解器可据此判断线、边、面或尺寸引用。
        return object;
    }

    //------ 线性零部件阵列-所选参考归属校验 开始------
    /**
     * 校验“所选参考”槽位是否允许写入当前对象。
     *
     * 设计说明：
     * 1. “所选参考”语义是源零部件上的局部几何参考，不是任意场景参考；
     * 2. 这里在选择入口统一拦截，避免错误对象进入控制器后再污染预览求解；
     * 3. 非“所选参考”槽位不参与该校验，保持方向、旋转轴和参考几何原有选择能力。
     *
     * @param activeSlot 当前激活槽位。
     * @param object 当前命中对象。
     * @returns 校验结果。
     */
    private validateSelectedReferenceSelection(
        activeSlot: LinearComponentPatternSelectionSlot,
        object: THREE.Object3D,
    ): { accepted: true } | { accepted: false; message: string } {
        if (!LinearComponentPatternEntitySelectionHandler.SELECTED_REFERENCE_SLOTS.has(activeSlot)) {
            return { accepted: true };
        }

        if (!this.isSelectedReferenceFeatureObject(object)) {
            return {
                accepted: false,
                message: LinearComponentPatternEntitySelectionHandler.SELECTED_REFERENCE_FEATURE_WARNING,
            };
        }

        const controller = this.entitySelectionManager.renderer.linearComponentPatternController;
        const componentObjectIds = controller?.getViewModel?.().state.componentObjectIds ?? [];
        if (componentObjectIds.length === 0) {
            return {
                accepted: false,
                message: LinearComponentPatternEntitySelectionHandler.SELECTED_REFERENCE_COMPONENT_WARNING,
            };
        }

        if (!this.isObjectOwnedByPatternComponent(object, componentObjectIds)) {
            return {
                accepted: false,
                message: LinearComponentPatternEntitySelectionHandler.SELECTED_REFERENCE_FEATURE_WARNING,
            };
        }

        return { accepted: true };
    }

    /**
     * 判断对象是否为“所选参考”允许的特征点、线或面。
     *
     * @param object 当前命中对象。
     * @returns 可作为所选参考时返回 `true`。
     */
    private isSelectedReferenceFeatureObject(object: THREE.Object3D): boolean {
        return isFeaturePoint(object) || isFeatureLine(object) || isFeatureFace(object);
    }

    /**
     * 判断当前特征对象是否归属于已选择的阵列源零部件。
     *
     * 设计说明：
     * 1. imported topology 代理通常挂在 FeatureResultMesh 或其拥有者下；
     * 2. 源零部件槽位保存的是装配语义根对象，因此需要沿对象父链向上追溯；
     * 3. 少数代理对象可能暂未挂入父链，此时补充使用 owning result mesh 与 objectId 兜底。
     *
     * @param object 当前命中对象。
     * @param componentObjectIds 已选源零部件对象 ID。
     * @returns 对象属于任一源零部件时返回 `true`。
     */
    private isObjectOwnedByPatternComponent(
        object: THREE.Object3D,
        componentObjectIds: readonly string[],
    ): boolean {
        const componentIdSet = new Set(componentObjectIds);
        const candidates = this.resolveSelectedReferenceOwnerCandidates(object);

        return candidates.some((candidate) => this.isObjectOrAncestorInIdSet(candidate, componentIdSet));
    }

    /**
     * 解析“所选参考”的归属候选对象。
     *
     * @param object 当前命中对象。
     * @returns 可用于归属判断的候选对象集合。
     */
    private resolveSelectedReferenceOwnerCandidates(object: THREE.Object3D): THREE.Object3D[] {
        const candidates: THREE.Object3D[] = [];
        const pushCandidate = (candidate: THREE.Object3D | null | undefined): void => {
            if (!candidate || candidates.includes(candidate)) {
                return;
            }
            candidates.push(candidate);
        };

        pushCandidate(object);
        pushCandidate(this.importedModelRaycastResolver.resolveTransformRootObject(object));
        pushCandidate(this.importedModelRaycastResolver.resolveSelectionObject(object));

        if (isFeaturePoint(object) || isFeatureLine(object) || isFeatureFace(object)) {
            pushCandidate(object.getOwningResultMesh());
            const featureObjectId = typeof object.objectId === 'string' ? object.objectId.trim() : '';
            if (featureObjectId.length > 0) {
                pushCandidate(this.entitySelectionManager.renderer.scene.getObjectByProperty('uuid', featureObjectId));
            }
        }

        return candidates;
    }

    /**
     * 判断对象自身或任一父级是否命中目标 ID 集合。
     *
     * @param object 起始对象。
     * @param targetIds 目标对象 ID 集合。
     * @returns 命中时返回 `true`。
     */
    private isObjectOrAncestorInIdSet(object: THREE.Object3D, targetIds: ReadonlySet<string>): boolean {
        let current: THREE.Object3D | null = object;
        while (current) {
            if (targetIds.has(current.uuid)) {
                return true;
            }
            current = current.parent;
        }
        return false;
    }

    /**
     * 通过 Viewer 的统一通知通道提示阵列选择错误。
     *
     * @param message 提示内容。
     */
    private notifyPatternSelectionWarning(message: string): void {
        this.entitySelectionManager.renderer.drawingNoticeCallback?.(message, 'warning');
    }
    //------ 线性零部件阵列-所选参考归属校验 结束------

    //------ 线性零部件阵列-参考几何垂直校验 开始------
    /**
     * 校验“参考几何”是否与当前阵列方向满足垂直关系。
     *
     * 设计说明：
     * 1. 面状参考的“参考几何垂直于阵列方向”体现为面法向与阵列方向平行；
     * 2. 线状参考自身必须与阵列方向垂直；
     * 3. 点参考没有方向语义，只作为终点位置参与投影，不参与该角度校验。
     *
     * @param activeSlot 当前激活槽位。
     * @param object 当前命中对象。
     * @returns 校验结果。
     */
    private validateReferenceGeometrySelection(
        activeSlot: LinearComponentPatternSelectionSlot,
        object: THREE.Object3D,
    ): { accepted: true } | { accepted: false; message: string } {
        if (!LinearComponentPatternEntitySelectionHandler.REFERENCE_GEOMETRY_SLOTS.has(activeSlot)) {
            return { accepted: true };
        }

        const patternDirectionWorld = this.resolvePatternDirectionForReferenceGeometry(activeSlot);
        if (!patternDirectionWorld) {
            return { accepted: true };
        }

        if (!this.isReferenceGeometryPerpendicularToDirection(object, patternDirectionWorld)) {
            return {
                accepted: false,
                message: LinearComponentPatternEntitySelectionHandler.REFERENCE_GEOMETRY_NOT_PERPENDICULAR_WARNING,
            };
        }

        return { accepted: true };
    }

    /**
     * 解析当前参考几何槽位对应的阵列方向。
     *
     * @param activeSlot 当前激活槽位。
     * @returns 阵列方向；无法解析时返回 `null`。
     */
    private resolvePatternDirectionForReferenceGeometry(
        activeSlot: LinearComponentPatternSelectionSlot,
    ): THREE.Vector3 | null {
        const directionSlot = activeSlot === LinearComponentPatternSelectionSlot.Direction1ReferenceGeometry
            ? LinearComponentPatternSelectionSlot.Direction1Reference
            : LinearComponentPatternSelectionSlot.Direction2Reference;
        const directionObject = this.entitySelectionManager.renderer.linearComponentPatternController
            ?.getReferenceObjectBySlot?.(directionSlot);
        if (!directionObject) {
            return null;
        }

        const resolvedDirection = this.directionReferenceResolver.resolve(directionObject);
        const rawDirection = resolvedDirection?.directionWorld ?? resolvedDirection?.planeNormalWorld;
        if (!rawDirection || rawDirection.lengthSq() <= LinearComponentPatternEntitySelectionHandler.REFERENCE_GEOMETRY_ORTHOGONAL_EPSILON) {
            return null;
        }
        return rawDirection.clone().normalize();
    }

    /**
     * 判断参考几何是否与阵列方向满足垂直关系。
     *
     * @param referenceGeometryObject 参考几何对象。
     * @param patternDirectionWorld 阵列方向。
     * @returns 满足关系时返回 `true`。
     */
    private isReferenceGeometryPerpendicularToDirection(
        referenceGeometryObject: THREE.Object3D,
        patternDirectionWorld: THREE.Vector3,
    ): boolean {
        if (isFeaturePoint(referenceGeometryObject)) {
            return true;
        }

        const resolvedReference = this.linearReferenceResolver.resolve(referenceGeometryObject);
        if (!resolvedReference) {
            return true;
        }

        if (resolvedReference.planeNormalWorld) {
            const normalizedPlaneNormal = resolvedReference.planeNormalWorld.clone().normalize();
            return this.areDirectionsParallel(normalizedPlaneNormal, patternDirectionWorld);
        }

        if (resolvedReference.directionWorld) {
            const normalizedReferenceDirection = resolvedReference.directionWorld.clone().normalize();
            return this.areDirectionsPerpendicular(normalizedReferenceDirection, patternDirectionWorld);
        }

        return true;
    }

    /**
     * 判断两个方向是否平行。
     *
     * @param left 左侧方向。
     * @param right 右侧方向。
     * @returns 平行时返回 `true`。
     */
    private areDirectionsParallel(left: THREE.Vector3, right: THREE.Vector3): boolean {
        return Math.abs(1 - Math.abs(left.dot(right))) <= LinearComponentPatternEntitySelectionHandler.REFERENCE_GEOMETRY_ORTHOGONAL_EPSILON;
    }

    /**
     * 判断两个方向是否垂直。
     *
     * @param left 左侧方向。
     * @param right 右侧方向。
     * @returns 垂直时返回 `true`。
     */
    private areDirectionsPerpendicular(left: THREE.Vector3, right: THREE.Vector3): boolean {
        return Math.abs(left.dot(right)) <= LinearComponentPatternEntitySelectionHandler.REFERENCE_GEOMETRY_ORTHOGONAL_EPSILON;
    }
    //------ 线性零部件阵列-参考几何垂直校验 结束------

    /**
     * 解析线性零部件阵列命令的当前点击命中。
     *
     * @param payload 原始事件。
     * @returns 命中结果。
     */
    private resolvePatternPick(payload: RawPointerEventPayload): THREE.Intersection<THREE.Object3D> | null {
        const activeSlot = this.resolveActiveSelectionSlot();
        if (activeSlot === LinearComponentPatternSelectionSlot.SkippedInstances) {
            return this.resolveSkipMarkerPick(payload);
        }
        const isReferenceSelectionSlot = activeSlot === LinearComponentPatternSelectionSlot.Direction1Reference
            || activeSlot === LinearComponentPatternSelectionSlot.Direction1RotationAxis
            || activeSlot === LinearComponentPatternSelectionSlot.Direction1ReferenceGeometry
            || activeSlot === LinearComponentPatternSelectionSlot.Direction1SelectedReference
            || activeSlot === LinearComponentPatternSelectionSlot.Direction2Reference
            || activeSlot === LinearComponentPatternSelectionSlot.Direction2ReferenceGeometry
            || activeSlot === LinearComponentPatternSelectionSlot.Direction2SelectedReference;
        if (!isReferenceSelectionSlot) {
            const componentPick = this.pickImportedModelAt(
                payload.clientX,
                payload.clientY,
                this.collectClickSelectionTargets(),
            );
            //------ 线性零部件阵列-零部件槽位候选过滤 开始------
            // 零部件槽位只接受 FeatureFace 和非拓扑代理对象；FeatureLine / FeaturePoint 不应触发零部件选择。
            return this.filterTopologyProxyPick(componentPick);
            //------ 线性零部件阵列-零部件槽位候选过滤 结束------
        }

        // 方向槽位需要允许基准面、特征线等普通实体，优先走统一实体拾取。
        const picked = this.pickEntityAt(
            payload.clientX,
            payload.clientY,
            [
                ...this.collectClickSelectionTargets(),
                ...this.collectVisibleDatumPlaneTargets(),
            ],
        ) ?? this.pickImportedModelAt(
            payload.clientX,
            payload.clientY,
            this.collectClickSelectionTargets(),
        );

        // 点击保留原始参考命中，交给 SDK 校验并反馈拒绝原因，不能把非法方向伪装成空白点击。
        return picked;
    }

    //------ 线性零部件阵列-方向箭头 开始------
    /**
     * 读取当前激活槽位。
     *
     * @returns 激活槽位。
     */
    private resolveActiveSelectionSlot(): LinearComponentPatternSelectionSlot {
        const controller = this.entitySelectionManager.renderer.linearComponentPatternController;
        return controller?.getViewModel?.().state.activeSelectionSlot
            ?? LinearComponentPatternSelectionSlot.Direction1Reference;
    }

    /**
     * 恢复已持久化的阵列选择高亮。
     */
    private restorePersistedPatternSelectionState(): void {
        const persistedObjects = this.resolveAllPersistedPatternObjects();
        if (persistedObjects.length === 0) {
            this.entitySelectionManager.updateSelectionState([]);
            return;
        }

        const persistedIntersections = persistedObjects.map((object) => ({
            distance: 0,
            object,
            point: object.getWorldPosition(new THREE.Vector3()),
        }));
        this.entitySelectionManager.updateSelectionState(persistedIntersections);
    }

    /**
     * 解析跳过实例小球点击命中。
     *
     * @param payload 原始事件。
     * @returns 命中结果。
     */
    private resolveSkipMarkerPick(payload: RawPointerEventPayload): THREE.Intersection<THREE.Object3D> | null {
        const controller = this.entitySelectionManager.renderer.linearComponentPatternController;
        const markerObjects = controller?.getSkipInstanceMarkerObjects() ?? [];
        if (markerObjects.length === 0) {
            return null;
        }
        return this.pickEntityAt(payload.clientX, payload.clientY, markerObjects);
    }

    /**
     * 计算 marker 的屏幕包围盒。
     *
     * @param object marker 对象。
     * @param camera 当前相机。
     * @param containerRect 容器包围盒。
     * @returns 屏幕包围盒；为空时返回 `null`。
     */
    private computeMarkerScreenRect(
        object: THREE.Object3D,
        camera: THREE.Camera,
        containerRect: DOMRect,
    ): { minX: number; minY: number; maxX: number; maxY: number } | null {
        const worldBox = new THREE.Box3().setFromObject(object);
        if (worldBox.isEmpty()) {
            return null;
        }

        const points: THREE.Vector3[] = [
            new THREE.Vector3(worldBox.min.x, worldBox.min.y, worldBox.min.z),
            new THREE.Vector3(worldBox.min.x, worldBox.min.y, worldBox.max.z),
            new THREE.Vector3(worldBox.min.x, worldBox.max.y, worldBox.min.z),
            new THREE.Vector3(worldBox.min.x, worldBox.max.y, worldBox.max.z),
            new THREE.Vector3(worldBox.max.x, worldBox.min.y, worldBox.min.z),
            new THREE.Vector3(worldBox.max.x, worldBox.min.y, worldBox.max.z),
            new THREE.Vector3(worldBox.max.x, worldBox.max.y, worldBox.min.z),
            new THREE.Vector3(worldBox.max.x, worldBox.max.y, worldBox.max.z),
        ];

        let minX = Number.POSITIVE_INFINITY;
        let minY = Number.POSITIVE_INFINITY;
        let maxX = Number.NEGATIVE_INFINITY;
        let maxY = Number.NEGATIVE_INFINITY;

        points.forEach((point) => {
            const projected = point.clone().project(camera);
            const screenX = ((projected.x + 1) * 0.5) * containerRect.width;
            const screenY = ((1 - projected.y) * 0.5) * containerRect.height;
            minX = Math.min(minX, screenX);
            minY = Math.min(minY, screenY);
            maxX = Math.max(maxX, screenX);
            maxY = Math.max(maxY, screenY);
        });

        if (!Number.isFinite(minX) || !Number.isFinite(minY) || !Number.isFinite(maxX) || !Number.isFinite(maxY)) {
            return null;
        }

        return { minX, minY, maxX, maxY };
    }

    /**
     * 判断外矩形是否包含内矩形。
     *
     * @param outer 外矩形。
     * @param inner 内矩形。
     * @returns 是否包含。
     */
    private patternRectangleContains(
        outer: { minX: number; minY: number; maxX: number; maxY: number },
        inner: { minX: number; minY: number; maxX: number; maxY: number },
    ): boolean {
        return outer.minX <= inner.minX
            && outer.minY <= inner.minY
            && outer.maxX >= inner.maxX
            && outer.maxY >= inner.maxY;
    }

    /**
     * 判断两个矩形是否相交。
     *
     * @param left 矩形 A。
     * @param right 矩形 B。
     * @returns 是否相交。
     */
    private patternRectanglesIntersect(
        left: { minX: number; minY: number; maxX: number; maxY: number },
        right: { minX: number; minY: number; maxX: number; maxY: number },
    ): boolean {
        return !(
            left.maxX < right.minX
            || left.minX > right.maxX
            || left.maxY < right.minY
            || left.minY > right.maxY
        );
    }

    /**
     * 根据当前方向参考对象更新对应方向箭头。
     *
     * 设计说明：
     * 1. 优先复用 `MoveAlongEntityReferenceResolver` 获取锚点与方向；
     * 2. 解析失败时回退到包围盒中心 + 对象朝向；
     * 3. 箭头尺寸采用"对象尺度"缩放，保证大小稳定可见。
     *
     * @param object 当前方向参考对象。
     * @param arrowSlotKey 目标箭头槽位键（DIRECTION1 或 DIRECTION2）。
     */
    private updateDirectionArrowForObject(object: THREE.Object3D, arrowSlotKey: string): void {
        const viewer = this.entitySelectionManager.renderer;
        const directionInfo = this.directionReferenceResolver.resolve(object);

        const anchorWorld = directionInfo?.anchorWorld;
        const directionWorld = directionInfo?.directionWorld?.clone()
            ?? directionInfo?.planeNormalWorld?.clone();

        // 失效圆边不能沿首段弦或世界 Z 轴显示假方向，箭头与实例求解同时失败关闭。
        if (!anchorWorld || !directionWorld || directionWorld.lengthSq() <= 1e-12) {
            viewer.patternDirectionArrowManager.clearArrow(arrowSlotKey);
            return;
        }

        const arrowSize = this.resolveDirectionArrowSize(anchorWorld, object);
            const bottomLength = Math.max(arrowSize.shaftLength * 0.03, arrowSize.shaftRadius * 0.3);
        const topLength = Math.max(arrowSize.shaftLength - bottomLength, arrowSize.headLength * 1.1);
            const anchorLift = Math.max(bottomLength + arrowSize.shaftRadius * 0.3, arrowSize.headLength * 0.08);
        viewer.patternDirectionArrowManager.setArrow(
            arrowSlotKey,
            {
                color: LinearComponentPatternEntitySelectionHandler.DIRECTION_ARROW_COLOR,
                anchorWorld,
                directionWorld,
                shaftRadius: arrowSize.shaftRadius,
                shaftLength: arrowSize.shaftLength,
                headRadius: arrowSize.headRadius,
                headLength: arrowSize.headLength,
                bottomLength,
                topLength,
                anchorLift,
                pixelSize: {
                    shaftRadiusPixels: LinearComponentPatternEntitySelectionHandler.DIRECTION_ARROW_SHAFT_RADIUS_PIXELS,
                    shaftLengthPixels: LinearComponentPatternEntitySelectionHandler.DIRECTION_ARROW_SHAFT_LENGTH_PIXELS,
                    headRadiusPixels: LinearComponentPatternEntitySelectionHandler.DIRECTION_ARROW_HEAD_RADIUS_PIXELS,
                    headLengthPixels: LinearComponentPatternEntitySelectionHandler.DIRECTION_ARROW_HEAD_LENGTH_PIXELS,
                    bottomLengthPixels: LinearComponentPatternEntitySelectionHandler.DIRECTION_ARROW_BOTTOM_LENGTH_PIXELS,
                    topLengthPixels: LinearComponentPatternEntitySelectionHandler.DIRECTION_ARROW_TOP_LENGTH_PIXELS,
                    anchorLiftPixels: LinearComponentPatternEntitySelectionHandler.DIRECTION_ARROW_ANCHOR_LIFT_PIXELS,
                    minWorldSize: 1e-5,
                },
            },
        );
    }

    /**
    * 计算方向箭头尺寸兜底值。
    *
    * 设计说明：
    * 1. 当前线性阵列方向箭头已优先使用屏幕空间像素尺寸；
    * 2. 这里仍保留世界尺寸兜底，供无相机/无容器等异常环境继续渲染；
    * 3. 同时可作为像素尺寸换算失败时的稳定后备值，避免箭头退化为不可见。
     *
     * @param _anchorWorld 保留参数（暂不使用）。
     * @param object 方向参考对象。
     * @returns 箭头尺寸。
     */
    private resolveDirectionArrowSize(
        _anchorWorld: THREE.Vector3,
        object: THREE.Object3D,
    ): {
        shaftRadius: number;
        shaftLength: number;
        headRadius: number;
        headLength: number;
    } {
        const referenceSize = Math.max(
            this.resolveReferenceFeatureSize(object),
            LinearComponentPatternEntitySelectionHandler.DIRECTION_ARROW_MIN_REFERENCE_SIZE,
        );

        // 与基准面法线箭头保持一致的比例系数，避免线性阵列方向箭头显著偏小。
        const headLength = Math.max(referenceSize * 0.08, referenceSize * 0.02);
        const shaftLength = Math.max(referenceSize * 0.16, headLength * 1.8);
        const shaftRadius = Math.max(referenceSize * 0.006, 0.001);
        const headRadius = Math.max(referenceSize * 0.022, shaftRadius * 1.8);

        return {
            shaftRadius,
            shaftLength,
            headRadius,
            headLength,
        };
    }

    /**
     * 解析方向参考对象的几何尺度。
     *
     * 设计说明：
     * 1. 线性参考优先使用真实线长；
     * 2. 面参考优先使用边界顶点包围盒短边，避免长条面导致箭头过长；
     * 3. 无法识别时回退到对象包围盒对角线。
     *
     * @param object 方向参考对象。
     * @returns 参考尺度。
     */
    private resolveReferenceFeatureSize(object: THREE.Object3D): number {
        if (isFeatureLine(object)) {
            const endpoints = object.getWorldEndpoints();
            if (endpoints) {
                const lineLength = endpoints.end.distanceTo(endpoints.start);
                if (lineLength > 1e-8) {
                    return lineLength;
                }
            }
        }

        if (isFeatureFace(object)) {
            const boundaryVertices = object.getWorldBoundaryVertices();
            if (boundaryVertices.length > 0) {
                this.tempBoundingBox.setFromPoints(boundaryVertices);
                if (!this.tempBoundingBox.isEmpty()) {
                    const size = this.tempBoundingBox.getSize(this.tempBoundingSize);
                    const components = [size.x, size.y, size.z].filter((value) => value > 1e-8);
                    const shortest = components.length > 0 ? Math.min(...components) : 0;
                    const diagonal = size.length();
                    const faceScale = Math.max(shortest, diagonal * 0.25);
                    if (faceScale > 1e-8) {
                        return faceScale;
                    }
                }
            }
        }

        this.tempBoundingBox.setFromObject(object);
        if (!this.tempBoundingBox.isEmpty()) {
            const diagonal = this.tempBoundingBox.getSize(this.tempBoundingSize).length();
            if (diagonal > 1e-8) {
                return diagonal;
            }
        }

        return 0.1;
    }

    //------ 线性零部件阵列-方向箭头 结束------
}
